import { useEffect, useRef, useState } from "react";
import {
  type CountSample,
  eventsPerSecond,
  PACE_WINDOW_MS,
  withinWindow,
} from "./flowMotion";

/** The pipeline's pace in events per second, read off its processed-events
 *  counter across polls: each poll adds a reading, readings older than the
 *  window fall away, and the pace is the counter's rise over the window.
 *  `updatedAt` is the poll's own timestamp, so a poll that found the same
 *  count still adds a reading and lets the pace fall to zero. Null while
 *  the pipeline is not running. */
export function useFlowPace(
  eventsProcessed: number | undefined,
  updatedAt: number,
  running: boolean,
): number | null {
  const samples = useRef<CountSample[]>([]);
  const [pace, setPace] = useState(0);

  useEffect(() => {
    const reading = readingOf(eventsProcessed, updatedAt, running);
    if (reading === null) {
      samples.current = [];
      setPace(0);
      return;
    }
    const kept = withinWindow(samples.current, reading.at, PACE_WINDOW_MS);
    kept.push(reading);
    samples.current = kept;
    setPace(eventsPerSecond(kept));
  }, [eventsProcessed, updatedAt, running]);

  return running ? pace : null;
}

/** One poll as a reading, or null when there is nothing to read: the
 *  pipeline is not running, the count is unknown, or no poll has landed. */
function readingOf(
  events: number | undefined,
  at: number,
  running: boolean,
): CountSample | null {
  if (!running) return null;
  if (events === undefined) return null;
  if (at === 0) return null;
  return { at, events };
}
